<template>
  <PrimeModal
    :is_small="false"
    :is_large="true"
    :prevent_enter_shortcut="true"
    :is_confirmed="!is_button_primary_disabled"
    data-test="project-modal"
    @actionPrimary="onSubmit"
    @actionSecondary="onBack"
    @update:visible="$emit('close')"
  >
    <template #title>
      {{ $t(`project.modals.add_project.step_${current_step}.title`) }}
    </template>
    <template
      v-if="$te(`project.modals.add_project.step_${current_step}.subtitle`)"
      #subtitle
    >
      {{ $t(`project.modals.add_project.step_${current_step}.subtitle`) }}
    </template>
    <template
      v-if="current_step === 1"
      #content
    >
      <div class="flex w-full flex-col gap-6">
        <FormField
          :label="$t('project.labels.projectName')"
          class="w-auto md:w-4/6 lg:w-3/6"
        >
          <InputText
            v-model="v$.project.attributes.name.$model"
            class="w-full"
            :invalid="v$.project.attributes.name.$error"
            data-test="project-name-input"
          />
          <FormError v-if="v$.project.attributes.name.$error && v$.project.attributes.name.required.$invalid">
            {{ $t('project.validation.needName') }}
          </FormError>
          <FormError v-else-if="v$.project.attributes.name.$error && v$.project.attributes.name.uniqueName.$invalid">
            {{ $t('validation.nameAlreadyTaken') }}
          </FormError>
        </FormField>

        <label class="flex cursor-pointer items-center gap-2 self-start">
          <ToggleSwitch
            :modelValue="project.attributes.skip_customer"
            data-test="project-template-toggle"
            @update:modelValue="toggleTemplateProject"
          />
          <span>{{ $t('project.labels.isTemplateProject') }}</span>
        </label>
      </div>
    </template>
    <template
      v-if="current_step === 3"
      #content
    >
      <section class="w-full">
        <div class="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          <div
            v-for="(level_option, level_key) in level_options"
            :key="level_key"
            class="flex p-2"
          >
            <Card
              :class="[
                'relative w-full transition-shadow duration-150 hover:shadow-md',
                level_option.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              ]"
              :data-test="`project-level-${level_key}`"
              :pt="{ body: { class: 'flex h-full flex-col p-4' } }"
              @click="toggleAttribute(level_option)"
            >
              <template #content>
                <div class="mb-3 flex items-center">
                  <AppIcon
                    :name="level_option.icon"
                    class="mr-2"
                  />
                  <span class="hidden font-semibold md:inline">
                    {{ $truncate(level_option.label, 12) }}
                  </span>
                  <span class="font-semibold md:hidden">
                    {{ $truncate(level_option.label, 32) }}
                  </span>
                </div>

                <p class="mb-12 grow">
                  {{ level_option.explainer }}
                </p>

                <div
                  v-if="!project.attributes[level_option.attribute] && !level_option.disabled"
                  class="absolute right-3 bottom-3 left-3 flex items-center justify-center rounded-full bg-success-500 px-3 py-1 text-xs font-medium text-white shadow-sm"
                >
                  <AppIcon
                    name="solid-check"
                    class="mr-2"
                  />
                  {{ $t('project.labels.selected') }}
                </div>

                <div
                  v-else-if="project.attributes[level_option.attribute] && !level_option.disabled"
                  class="absolute right-3 bottom-3 left-3 flex items-center justify-center rounded-full bg-surface-600 px-3 py-1 text-xs font-medium text-white shadow-sm"
                >
                  {{ $t('project.labels.unselected') }}
                </div>

                <div
                  v-else-if="level_option.disabled"
                  class="absolute right-3 bottom-3 left-3 flex items-center justify-center rounded-full bg-success-500 px-3 py-1 text-xs font-medium text-white shadow-sm"
                >
                  {{ $t('project.labels.included') }}
                </div>
              </template>
            </Card>
          </div>
        </div>
      </section>
    </template>

    <template
      v-if="current_step === 4"
      #content
    >
      <div
        v-if="$flipper.enabled($FT.TEMPLATE_PROJECTS) && !project.attributes.skip_customer"
        class="mb-6 h-[250px] w-full"
      >
        <SelectProject
          :active="project_template"
          :label="$t('project.modals.add_project.step_4.placeholder')"
          :projects_props="projects_template"
          @changeSelect="(pt) => (project_template = pt)"
        />
        <section
          v-if="project_template"
          class="mt-5 ml-5 flex flex-col gap-3"
        >
          <label
            v-for="feature_option in feature_options"
            :key="feature_option.key"
            class="flex cursor-pointer items-center gap-2 self-start"
          >
            <Checkbox
              v-model="features[feature_option.key]"
              binary
              :data-test="`project-feature-${feature_option.key}`"
            />
            <span>{{ feature_option.label }}</span>
          </label>
        </section>
        <br />
        <Message
          v-if="project_template && !project.attributes.skip_unit"
          severity="info"
        >
          <template #icon>
            <AppIcon
              name="solid-circle-info"
              size="lg"
            />
          </template>
          {{ $t('project.modals.add_project.step_4.info') }}
        </Message>
      </div>
      <UpsellBox
        v-else-if="!$flipper.enabled($FT.TEMPLATE_PROJECTS)"
        feature="project_templates"
      >
        <template #guidance_text>
          <div
            class="text-left"
            v-html="$t(`upsell.project_templates.description`)"
          />
        </template>
      </UpsellBox>
    </template>
    <template #ButtonPrimary>
      {{ button_primary_text }}
    </template>
    <template
      v-if="current_step > 1"
      #ButtonSecondary
    >
      {{ $t('modal.actions.goBack') }}
    </template>
  </PrimeModal>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import PrimeModal from '@/app/shared_components/modals/ThePrimeModal.vue';
import FormField from '@/app/shared_components/forms/FormField.vue';
import FormError from '@/app/shared_components/forms/FormError.vue';
import { required } from '@vuelidate/validators';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import SelectProject from '@/app/shared_components/selects/SelectProject.vue';
import UpsellBox from '@/app/_employees/components/UpsellBox.vue';
import { PROJECT_TYPES, Tenant } from '@/app/data/model_constants';
import { Confetti } from 'vue-confetti';
import { FT } from '@/app/data/flipper_constants';
import { mapActions, mapGetters } from '@/store/lib/vue3helpers.js';
import { useToasts } from '@/app/util/toasts';
import INJECT_KEYS from '@/app/data/inject_keys';

const emit = defineEmits(['close']);

const { current_client, projects } = mapGetters(['current_client', 'projects']);
const { CREATE_PROJECT, DUPLICATE_PROJECT, AUTOCOMPLETE_PROJECTS } = mapActions([
  'CREATE_PROJECT',
  'DUPLICATE_PROJECT',
  'AUTOCOMPLETE_PROJECTS',
]);
const { t } = useI18n();
const router = useRouter();
const flipper = inject(INJECT_KEYS.FLIPPER);
const { toast_action_failed } = useToasts();

const current_step = ref(1);
const project = ref({
  attributes: {
    name: '',
    skip_phase: null,
    skip_lot: null,
    skip_building: null,
    skip_unit: null,
    skip_customer: null,
    status: 'enabled',
  },
});
const project_template = ref(null);
const features = ref({
  milestones: true,
  tasks: true,
  documents: true,
  decisions: true,
  surveys: true,
  question_lists: true,
  announcements: true,
});
const projects_autocomplete = ref([]);
const is_checking_name = ref(false);

const v$ = useVuelidate(
  {
    project: {
      attributes: {
        name: {
          required,
          uniqueName(val) {
            if (!val) {
              return true;
            }
            const name_exists_autocomplete = _.some(
              projects_autocomplete.value,
              (project_found) => project_found.attributes.name.toLowerCase() === val.toLowerCase(),
            );

            return !name_exists_autocomplete;
          },
        },
      },
    },
  },
  { project },
);

const is_button_primary_disabled = computed(() => {
  if (current_step.value === 1) {
    return v$.value.project.attributes.name.$invalid || is_checking_name.value;
  }
  return false;
});

const projects_template = computed(() =>
  // TODO: this needs pagination with backend filtering
  _.chain(projects.value)
    .filter({ attributes: { skip_customer: true } })
    .orderBy(['attributes.name'], ['asc'])
    .value(),
);

const button_primary_text = computed(() => {
  if (project.value.attributes.skip_customer || current_step.value === 4) {
    return t('project.actions.submit');
  }

  return t('project.actions.next');
});

const level_options = computed(() => ({
  projects: {
    label: project.value.attributes.name,
    value: true,
    disabled: true,
    explainer: t('project.labels.levelProjectExplainer'),
    icon: 'solid-layer-group',
  },
  phase: {
    attribute: 'skip_phase',
    label: t('levels.phase'),
    value: !project.value.attributes.skip_phase,
    explainer: t('project.labels.levelPhaseExplainer'),
    icon: 'solid-calendar-days',
  },
  lot: {
    attribute: 'skip_lot',
    label: t('levels.lot'),
    value: !project.value.attributes.skip_lot,
    explainer: t('project.labels.levelPlotExplainer'),
    icon: 'solid-chart-tree-map',
  },
  building: {
    attribute: 'skip_building',
    label: t('levels.building'),
    value: !project.value.attributes.skip_building,
    explainer: t('project.labels.levelBuildingExplainer'),
    icon: 'solid-building',
  },
  units: {
    label: t('levels.unit'),
    value: true,
    disabled: true,
    explainer: t('project.labels.levelUnitExplainer'),
    icon: 'solid-house',
  },
}));

const feature_options = computed(() =>
  [
    { key: 'milestones', label: t('navbar.employee.project.milestones'), enabled: flipper.enabled(FT.MILESTONES) },
    {
      key: 'tasks',
      label: t('navbar.employee.project.tasks'),
      enabled: flipper.enabled(FT.TODOS) || flipper.enabled(FT.TASKS),
    },
    { key: 'documents', label: t('navbar.employee.project.documents'), enabled: flipper.enabled(FT.DOCUMENTS) },
    {
      key: 'decisions',
      label: t('navbar.employee.project.decisions'),
      enabled: flipper.enabled(FT.DECISIONS_CORE) && project.value.attributes.skip_unit,
    },
    { key: 'announcements', label: t('navbar.employee.project.news'), enabled: flipper.enabled(FT.ANNOUNCEMENTS) },
    {
      key: 'surveys',
      label: t('navbar.employee.project.surveys'),
      enabled: flipper.enabled(FT.SURVEYS) && project.value.attributes.skip_unit,
    },
    {
      key: 'question_lists',
      label: t('navbar.employee.project.question_lists'),
      enabled: flipper.enabled(FT.FAQS),
    },
  ].filter((feature_option) => feature_option.enabled),
);

const project_type = computed(() => current_client.value.attributes.project_type);

const toggleAttribute = (level_option) => {
  if (level_option.disabled) return;
  project.value.attributes[level_option.attribute] = !project.value.attributes[level_option.attribute];
};

const initSUP = () => {
  project.value.attributes.skip_phase = true;
  project.value.attributes.skip_lot = true;
  project.value.attributes.skip_building = true;
  project.value.attributes.skip_unit = true;
  project.value.attributes.skip_customer = false;
  project.value.attributes.dedicated = false;
};

const initTUP = () => {
  initSUP();
  project.value.attributes.skip_customer = true;
  project.value.attributes.dedicated = false;
};

const initMUP = () => {
  project.value.attributes.skip_phase = true;
  project.value.attributes.skip_lot = true;
  project.value.attributes.skip_building = true;
  project.value.attributes.skip_unit = false;
  project.value.attributes.skip_customer = false;
};

const toggleTemplateProject = (is_template) => {
  if (is_template) {
    initTUP();
  } else if (project_type.value === PROJECT_TYPES.SUP) {
    initSUP();
  } else {
    initMUP();
  }
};

const createProject = async () => {
  try {
    const project_created = await CREATE_PROJECT({ record: project.value });
    if (!project_created) return;
    if (project_template.value) {
      await DUPLICATE_PROJECT({
        record: {
          id_template: project_template.value.id,
          id_duplicate: project_created.id,
          attributes: {
            features: { ...features.value, todo_lists: features.value.tasks },
          },
        },
      });
    }
    const route_name = project.value.attributes.skip_customer
      ? ROUTES_EMPLOYEES.PROJECT_MILESTONES
      : ROUTES_EMPLOYEES.PROJECT_INFORMATION_LEVELS;
    if (!project.value.attributes.skip_customer) {
      try {
        const confetti = new Confetti();
        confetti.start();
        setTimeout(() => confetti.stop(), 5000);
      } catch {
        // non-critical, ignore confetti errors
      }
    }
    router.push({
      name: route_name,
      params: { project_id: project_created.id, level_type: project_created.type, level_id: project_created.id },
    });
  } catch (e) {
    toast_action_failed({ error: e });
  } finally {
    emit('close');
  }
};

const onSubmit = async () => {
  project.value.attributes.name = project.value.attributes.name.trim();
  if (current_step.value === 1 && project.value.attributes.name) {
    try {
      is_checking_name.value = true;
      const results = await AUTOCOMPLETE_PROJECTS({ name_cont: project.value.attributes.name });
      projects_autocomplete.value = results || [];
    } catch {
      projects_autocomplete.value = [];
    } finally {
      is_checking_name.value = false;
    }
  }
  v$.value.$touch();
  if (!v$.value.$invalid) {
    switch (current_step.value) {
      // define name and whether it's a template project
      case 1:
        // project template => single step, immediately create project
        if (project.value.attributes.skip_customer) return createProject();
        if (project_type.value === PROJECT_TYPES.SUP) {
          if (flipper.enabled(FT.TEMPLATE_PROJECTS)) {
            current_step.value = 4;
            return;
          }
          return createProject();
        }
        current_step.value = 3;
        return;

      // mup project: define sub levels
      case 3:
        current_step.value = 4;
        return;

      // mup/sup project: copy from template (optional)
      case 4:
        return createProject();
      default:
    }
    throw new Error('Invalid step');
  }
};

const onBack = () => {
  if (current_step.value === 4) {
    // SUP projects (skip_unit) come straight from step 1, MUP projects from step 3
    current_step.value = project.value.attributes.skip_unit ? 1 : 3;
    return;
  }
  if (current_step.value === 3) {
    current_step.value = 1;
    return;
  }
  current_step.value -= 1;
};

if (project_type.value === PROJECT_TYPES.SUP) {
  initSUP();
} else {
  initMUP();
}
</script>
